export default {
  nav: {
    lite: 'Lite',
    new: 'New',
  },
  footer: {
    links: {
      lite: 'Geofleet Lite',
    },
  },
  litePage: {
    seoTitle: 'Geofleet Lite — Simple Delivery Management Software for Small Businesses',
    seoDescription:
      'Geofleet Lite is single-hub delivery management from $49 a month for 3 drivers and 500 orders. Lite Plus adds route planning, auto-allocation and WhatsApp automation for $99. Lite Autopilot adds AI Copilot and Autopilot for $179. Save up to 20% billed annually.',
    breadcrumb: 'Geofleet Lite',

    heroBadge: 'Geofleet Lite',
    heroEyebrow: 'Simple delivery management for small businesses',
    heroTitle: 'Your hub. Your drivers.',
    heroTitleHighlight: 'Every delivery on one screen.',
    /** Direct answer paragraph — quotable by search and AI answer engines. */
    heroLead:
      'Geofleet Lite is delivery software for small businesses with a few drivers and one pickup point: a shop, kitchen, pharmacy or warehouse. Add orders, plan the day’s routes, watch drivers on a live map, and send every customer a tracking link. No spreadsheets, no WhatsApp group chasing.',
    ctaPrimary: 'Get started with Lite',
    ctaSecondary: 'See how it works',
    heroFacts: [
      { icon: 'ri-price-tag-3-line', label: 'Plans from $49/month' },
      { icon: 'ri-user-line', label: '3 drivers included' },
      { icon: 'ri-file-list-3-line', label: '500 orders included' },
    ],
    heroVisualAlt: 'Geofleet Lite dashboard with today\'s orders, delivery status, a Copilot brief and late-delivery alerts',
    heroDriverAlt: 'Geofleet Lite driver app showing the current stop, cash to collect and today\'s stops',
    heroTrackAlt: 'Customer tracking page showing the driver\'s live position and arrival time',
    screenshotOf: 'Geofleet Lite screen: {{title}}',

    problemKicker: 'Sound familiar?',
    problemTitle: 'Most small delivery teams run on chat groups and memory',
    problems: [
      { icon: 'ri-whatsapp-line', title: 'Orders scattered across chats', text: 'Orders come in by phone, WhatsApp and your store, and someone copies them into a sheet.' },
      { icon: 'ri-route-line', title: 'Routes planned in your head', text: 'Drivers zig-zag across town because nobody has time to sort 30 stops by hand.' },
      { icon: 'ri-phone-line', title: '“Where is my order?” calls', text: 'Customers call the shop, the shop calls the driver, and everyone waits.' },
      { icon: 'ri-money-dollar-circle-line', title: 'Cash that doesn’t add up', text: 'Cash on delivery gets settled at the end of the day from notes and memory.' },
    ],

    setupKicker: 'Get going today',
    setupTitle: 'Five quick steps, then send your first delivery',
    setupIntro: 'Lite asks only what it needs to run your deliveries. You can change everything later in Settings.',
    setupSteps: [
      { icon: 'ri-store-2-line', title: 'Your business', text: 'Name, logo and support phone, so customers recognise you.' },
      { icon: 'ri-map-pin-2-line', title: 'Pickup location', text: 'Your hub: where every route starts and ends.' },
      { icon: 'ri-time-line', title: 'Operating hours', text: 'When your drivers deliver.' },
      { icon: 'ri-shield-check-line', title: 'Delivery rules', text: 'Proof of delivery, retries and order numbers.' },
      { icon: 'ri-message-3-line', title: 'Customer messages', text: 'Choose SMS, WhatsApp or email for tracking links.' },
    ],

    dayKicker: 'A day with Lite',
    dayTitle: 'Everything a one-hub delivery business does, on one screen',
    dayItems: [
      {
        icon: 'ri-file-list-3-line',
        title: 'Take orders',
        text: 'Add an order in seconds with address search, a delivery slot and cash-on-delivery amount, or upload a CSV for the whole day.',
        points: ['Address autocomplete', 'Frequent-customer search', 'CSV import'],
      },
      {
        icon: 'ri-route-line',
        title: 'Plan routes',
        text: 'Pick today’s orders and drivers, press Optimise, then drag stops to fine-tune before you publish to drivers’ phones.',
        points: ['One-click optimise from your hub', 'Reorder or move stops', 'Publish to the driver app'],
      },
      {
        icon: 'ri-map-pin-time-line',
        title: 'Track live',
        text: 'See every driver and order on one map with live ETAs, and reassign or share a link straight from the map.',
        points: ['Drivers and orders views', 'Live ETA per stop', 'Needs-attention list'],
      },
      {
        icon: 'ri-shield-check-line',
        title: 'Prove delivery and collect cash',
        text: 'Drivers capture proof at the door and record cash collected. You see who holds how much and settle it in a tap.',
        points: ['Photo, signature or recipient name', 'Cash held per driver', 'Settle cash and download reports'],
      },
    ],

    connectedKicker: 'Connected and automated',
    connectedTitle: 'Orders flow in. Autopilot keeps work moving.',
    connectedIntro: 'Lite Plus brings in orders from WhatsApp, Shopify and WooCommerce and allocates them automatically. Lite Autopilot adds AI Copilot and Autopilot to run the repetitive steps for you.',
    connectedItems: [
      { icon: 'ri-shopping-bag-line', title: 'Shopify', text: 'Import new orders automatically and keep delivery status connected to the storefront.' },
      { icon: 'ri-store-line', title: 'WooCommerce', text: 'Turn WooCommerce orders into delivery jobs without copying addresses into another system.' },
      { icon: 'ri-whatsapp-line', title: 'WhatsApp automation', text: 'Take orders over WhatsApp, send live tracking updates and let customers reschedule or cancel in the chat.' },
      { icon: 'ri-magic-line', title: 'Autopilot', text: 'Allocate waiting orders, prepare routes and run repeatable dispatch workflows with guardrails you control.' },
      { icon: 'ri-links-line', title: 'More ways to connect', text: 'Import orders by CSV on any plan, or connect your own systems with the API and webhooks from Lite Plus.' },
    ],

    hubKicker: 'Built for one hub',
    hubTitle: 'Made for how small delivery businesses actually work',
    hubPoints: [
      { icon: 'ri-home-4-line', title: 'One pickup point, set once', text: 'Every order starts at your hub, so you never type a pickup address.' },
      { icon: 'ri-user-smile-line', title: 'Customers get a tracking link', text: 'Branded with your logo and colour. No app for customers to install.' },
      { icon: 'ri-smartphone-line', title: 'A driver app that keeps it simple', text: 'Current stop, navigate, call and proof of delivery. That’s it.' },
      { icon: 'ri-bar-chart-2-line', title: 'Today’s numbers at a glance', text: 'Delivered, failed, on time and what needs you next, the moment you open Lite.' },
    ],

    industriesTitle: 'Popular with',
    industries: [
      { label: 'Restaurants & cloud kitchens', href: '/industries/restaurants' },
      { label: 'Pharmacies', href: '/industries/pharmacies' },
      { label: 'Grocery & dark stores', href: '/industries/grocery' },
      { label: 'Florists', href: '/industries/flowers' },
      { label: 'Laundry', href: '/industries/laundry' },
      { label: 'Water delivery', href: '/industries/water' },
    ],

    pricingKicker: 'Lite pricing',
    pricingTitle: 'Three plans. Pick the one that fits your team.',
    pricingIntro: 'Choose by team size, monthly order volume and how much dispatch you want automated. Pay monthly, or save with quarterly or annual billing.',
    pricingColumns: { plan: 'Plan', drivers: 'Drivers', orders: 'Orders / month', includes: 'Included highlights', price: 'Price' },
    litePlan: {
      name: 'Lite',
      fit: 'For a small local delivery team',
      drivers: '3 included',
      orders: '500 included',
      includes: 'Orders · inventory · live tracking · proof of delivery · reporting',
    },
    litePlusPlan: {
      name: 'Lite Plus',
      badge: 'Most popular',
      fit: 'For a busy single-hub team',
      drivers: '10 included',
      orders: '1,000 included',
      includes: 'Route planning · auto-allocation · WhatsApp automation · API & webhooks',
    },
    liteAutopilotPlan: {
      name: 'Lite Autopilot',
      fit: 'For a high-volume hub that wants dispatch to run itself',
      drivers: '25 included',
      orders: '2,000 included',
      includes: 'AI Copilot · Autopilot · unlimited staff seats',
    },
    perMonth: 'per month',
    pricingBoundaryTitle: 'One hub only.',
    pricingBoundaryText: 'If you add another location, move to Geofleet Command.',

    compareKicker: 'Lite or Command?',
    compareTitle: 'Start with Lite. Grow into Geofleet without starting over.',
    compareIntro:
      'Lite runs on the same Geofleet platform. When you add a second hub or want AI dispatch, you upgrade your plan and keep all your orders, drivers and history.',
    compareColFeature: 'What you need',
    compareColLite: 'Geofleet Lite',
    compareColPlatform: 'Geofleet Command',
    compareRows: [
      { feature: 'Price', lite: '$49 Lite · $99 Lite Plus · $179 Lite Autopilot', platform: 'From $19 per driver / month' },
      { feature: 'Drivers', lite: '3 · 10 · 25 included', platform: '10-driver minimum' },
      { feature: 'Orders', lite: '500 · 1,000 · 2,000 per month', platform: '5,000+ based on plan and drivers' },
      { feature: 'Hubs', lite: 'One', platform: 'Multi-hub and multi-leg' },
      { feature: 'Live tracking and proof of delivery', lite: 'yes', platform: 'yes' },
      { feature: 'Route planning and auto-allocation', lite: 'From Lite Plus', platform: 'yes' },
      { feature: 'WhatsApp ordering and automation', lite: 'From Lite Plus', platform: 'yes' },
      { feature: 'API and webhooks', lite: 'From Lite Plus', platform: 'yes' },
      { feature: 'Customer tracking links', lite: 'yes', platform: 'yes' },
      { feature: 'Shopify and WooCommerce', lite: 'From Lite Plus', platform: 'From Professional' },
      { feature: 'AI Copilot and Autopilot', lite: 'Lite Autopilot', platform: 'From Professional' },
      { feature: 'Full AI agent suite', lite: 'no', platform: 'yes' },
      { feature: 'Carrier allocation, SLAs and AI Command Centre', lite: 'no', platform: 'yes' },
    ],
    compareYes: 'Included',
    compareNo: 'Not included',
    compareCta: 'Compare all plans',

    guidesTitle: 'Guides for single-hub teams',
    guides: [
      { label: 'Meet Geofleet Lite', href: '/blog/introducing-geofleet-lite' },
      { label: 'Single-hub delivery management: a daily playbook', href: '/blog/single-hub-delivery-management-guide' },
      { label: 'Managing deliveries on a WhatsApp group? When to move on', href: '/blog/whatsapp-group-delivery-management-alternative' },
      { label: 'How to plan delivery routes for a small business', href: '/blog/how-to-plan-delivery-routes-small-business' },
    ],

    faqTitle: 'Geofleet Lite FAQ',
    faqs: [
      {
        question: 'What is Geofleet Lite?',
        answer:
          'Geofleet Lite is a simple version of Geofleet for small businesses that deliver from one hub. It covers orders, route planning, live driver tracking, customer tracking links, proof of delivery and cash-on-delivery management.',
      },
      {
        question: 'How much does Geofleet Lite cost?',
        answer: 'Lite costs $49 a month with 3 drivers and 500 orders. Lite Plus costs $99 a month with 10 drivers and 1,000 orders. Lite Autopilot costs $179 a month with 25 drivers and 2,000 orders. Quarterly billing saves about 10% and annual billing up to 20%. All three plans are for one hub.',
      },
      {
        question: 'Is Geofleet Lite free?',
        answer: 'No. Lite starts at $49 a month with 3 drivers and 500 orders included. You can try it for 7 days with up to 100 orders before choosing a plan.',
      },
      {
        question: 'Who is Geofleet Lite for?',
        answer:
          'Restaurants, pharmacies, grocers, florists, laundries, water suppliers and other local businesses that send their own drivers out from a single shop, kitchen or warehouse.',
      },
      {
        question: 'Do my drivers need a special app?',
        answer: 'Drivers use the Geofleet driver app on their phone to see their stops, navigate, and capture proof of delivery. Customers do not need an app; they get a tracking link.',
      },
      {
        question: 'What happens when I outgrow Lite?',
        answer:
          'Lite is built for one hub. When you add a second hub, need multi-leg operations or want the full AI agent suite, you move to Geofleet Command and keep your operational history.',
      },
      {
        question: 'Can I import my existing orders?',
        answer: 'Yes. Add orders one by one, upload a CSV, or use Lite Plus to sync orders from Shopify and WooCommerce.',
      },
    ],

    ctaTitle: 'Send your first delivery with Lite today',
    ctaText: 'Start at $49 a month for 3 drivers and 500 orders. Move up to Lite Plus for route planning, auto-allocation and WhatsApp automation, or Lite Autopilot to let AI run dispatch.',
    ctaTalk: 'Talk to us',
  },
  home: {
    liteTeaser: {
      heroPill: 'Geofleet Lite for one-hub teams, from $49/month',
      badge: 'New',
      title: 'Running deliveries from one shop or warehouse?',
      text: 'Meet Geofleet Lite: orders, routes, live tracking and proof of delivery in one simple app, from $49 a month for 3 drivers and 500 orders.',
      cta: 'Explore Geofleet Lite',
    },
  },
};
